/**
 * Login Screen 1: Email/Phone Input
 *
 * First screen of Cognito login flow:
 * 1. User enters email or phone number
 * 2. Click "Continue"
 * 3. onSubmit callback handles check-user logic
 */

import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface LoginScreen1Props {
  onSubmit: (identifier: string) => Promise<void>;
  /** Open the Register step for the chosen persona (producer or buyer). */
  onRegister?: (userType: 'producer' | 'buyer') => void;
  error: string | null;
  identifier: string;
  onIdentifierChange: (identifier: string) => void;
}

export function LoginScreen1({
  onSubmit,
  onRegister = undefined,
  error,
  identifier,
  onIdentifierChange,
}: LoginScreen1Props) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit(identifier);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Phone Number or Email"
            variant="outlined"
            value={identifier}
            onChange={(e) => onIdentifierChange(e.target.value)}
            placeholder="1234567890 or you@example.com "
            disabled={loading}
            autoFocus
            required
            helperText="We'll use this to find your account and send you a sign-in code if needed."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !identifier}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Checking...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </Stack>
      </form>

      {onRegister && (
        <Stack spacing={0.5}>
          <Typography variant="body2" align="center">
            Don&apos;t have an account?{' '}
            <Link
              component="button"
              type="button"
              data-testid="register-producer"
              onClick={() => onRegister('producer')}
              disabled={loading}
            >
              Register as a producer
            </Link>
          </Typography>
          {/* "Create a buyer account" was removed when the buyer marketplace
              was switched off (PRO-784). Processors add their customers. */}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" align="center">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </Typography>
    </Stack>
  );
}
